import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, Subscription, from } from 'rxjs';
import { take, map, startWith, switchMap, mergeMap } from 'rxjs/operators';
import { HttpClient, HttpContext } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { User } from '../../../../core/interfaces/user.interface';
import { AppState } from '../../../../store/app.state';
import { UserService } from '../../../../core/services/user.service';
import { UpdateUserDto } from '../../../../core/interfaces/user.interface';
import { ToastService } from '../../../../core/services/toast.service';
import { updateCurrentUserSuccess } from '../../../../store/auth/auth.actions';
import { IdentityCardService } from '../../../../core/services/identity-card.service';
import { DocumentService } from '../../../../core/services/document.service';
import { SKIP_AUTH } from '../../../../core/interceptors/auth.interceptor';

// Validateur personnalisé pour empêcher les espaces vides
function noWhitespaceValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (control.value && typeof control.value === 'string') {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }
  return null;
}

// Validateur combiné pour les champs obligatoires sans espaces
function requiredNoWhitespaceValidator(
  control: AbstractControl
): ValidationErrors | null {
  const requiredError = Validators.required(control);
  if (requiredError) {
    return requiredError;
  }
  return noWhitespaceValidator(control);
}

@Component({
  selector: 'app-general-info',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './general-info.component.html',
  styleUrls: ['./general-info.component.scss'],
})
export class GeneralInfoComponent implements OnInit, OnDestroy {
  profileForm: FormGroup;
  currentUser$: Observable<User | null>;
  hasChanges$: Observable<boolean>;
  initialFormData: any = null;
  private isPopulating = false;
  isLoading = false;

  qrUrl: string | null = null;
  qrLoading = false;
  qrGenerating = false;
  qrEnlarged = false;
  private qrPassportNumber: string | null = null;
  private sub = new Subscription();

  private store = inject(Store<AppState>);
  private cdr = inject(ChangeDetectorRef);
  private userService = inject(UserService);
  private toastService = inject(ToastService);
  private identityCardService = inject(IdentityCardService);
  private documentService = inject(DocumentService);
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);

  constructor(private fb: FormBuilder) {
    this.currentUser$ = this.store.select((state) => state.auth.currentUser);

    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required, noWhitespaceValidator]],
      lastName: ['', [Validators.required, noWhitespaceValidator]],
      passportNumber: ['', [Validators.required, noWhitespaceValidator]],
      phone: ['', [Validators.required, noWhitespaceValidator]],
      email: ['', [Validators.email]],
      address: ['', noWhitespaceValidator],
      dateOfBirth: ['', Validators.required],
      sex: ['', Validators.required],
      healthUniqueNumber: [{ value: '', disabled: true }],
    });

    this.hasChanges$ = this.profileForm.valueChanges.pipe(
      startWith(this.profileForm.value),
      map((currentValue) => {
        if (this.isPopulating || !this.initialFormData) return false;
        return (
          JSON.stringify(currentValue) !== JSON.stringify(this.initialFormData)
        );
      })
    );
  }

  ngOnInit() {
    this.sub.add(
      this.currentUser$.subscribe((user) => {
        if (user) {
          this.populateForm(user);
          if (user.passportNumber && user.passportNumber !== this.qrPassportNumber) {
            this.qrPassportNumber = user.passportNumber;
            this.loadQrCode(user.passportNumber);
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private loadQrCode(passportNumber: string): void {
    this.qrLoading = true;
    this.qrUrl = null;
    this.sub.add(
      this.identityCardService.getIdentityCardDocument(passportNumber).pipe(
        switchMap((doc) => this.documentService.getPresignedUrl(doc.url)),
        mergeMap(({ url }) => this.http.get(url, {
          responseType: 'blob',
          context: new HttpContext().set(SKIP_AUTH, true),
        })),
        mergeMap((blob) => from(this.blobToDataUrl(blob)))
      ).subscribe({
        next: (dataUrl) => {
          this.qrUrl = dataUrl;
          this.qrLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.qrUrl = null;
          this.qrLoading = false;
          this.cdr.detectChanges();
        },
      })
    );
  }

  generateQrCode(): void {
    if (!this.qrPassportNumber || this.qrGenerating) return;
    this.qrGenerating = true;
    const passportNumber = this.qrPassportNumber;
    this.sub.add(
      this.identityCardService.generateIdentityCard(passportNumber).subscribe({
        next: () => {
          this.qrGenerating = false;
          this.toastService.success('Code QR généré avec succès');
          this.loadQrCode(passportNumber);
        },
        error: () => {
          this.qrGenerating = false;
          this.toastService.error('Impossible de générer le QR code');
          this.cdr.detectChanges();
        },
      })
    );
  }

  private populateForm(user: User) {
    this.isPopulating = true;

    const primaryPhone = user.phoneNumbers?.find((phone) => phone.isPrimary);
    const phoneNumber = primaryPhone
      ? `${primaryPhone.countryCode}${primaryPhone.number}`
      : '';

    const currentAddress = user.addresses?.find((addr) => addr.isCurrent);
    const address = currentAddress ? currentAddress.street : '';

    const birthdayParts = user.birthday?.split('/');
    const formattedBirthday =
      birthdayParts?.length === 3
        ? `${birthdayParts[2]}-${birthdayParts[1].padStart(
            2,
            '0'
          )}-${birthdayParts[0].padStart(2, '0')}`
        : '';

    const userData = {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      passportNumber: user.passportNumber || '',
      phone: phoneNumber,
      email: user.email || '',
      address: address,
      dateOfBirth: formattedBirthday,
      sex: user.sex || '',
      healthUniqueNumber: user.healthUniqueNumber?.number || '',
    };

    this.initialFormData = { ...userData };

    setTimeout(() => {
      this.profileForm.patchValue(userData);
      this.cdr.detectChanges();

      setTimeout(() => {
        this.isPopulating = false;
      }, 100);
    }, 0);

    setTimeout(() => {
      Object.keys(userData).forEach((key) => {
        const control = this.profileForm.get(key);
        if (control) {
          control.setValue(userData[key as keyof typeof userData]);
        }
      });
      this.cdr.detectChanges();
    }, 100);
  }

  private formatDateForAPI(dateString: string): string {
    if (!dateString) return '';

    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateString;
  }

  onSubmit() {
    if (this.profileForm.valid) {
      this.isLoading = true;
      const formData = this.profileForm.value;

      const updateData: UpdateUserDto = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        sex: formData.sex,
        birthday: this.formatDateForAPI(formData.dateOfBirth),
        address: {
          country: { code: 'CM', label: 'Cameroun' },
          region: '',
          departement: '',
          commune: '',
          street: formData.address,
        },
      };

      this.currentUser$.pipe(take(1)).subscribe((user) => {
        if (user) {
          this.userService.updateUser(user._id, updateData).subscribe({
            next: (updatedUser) => {
              this.toastService.success(
                'Profil mis à jour avec succès !',
                'Succès'
              );

              // Mettre à jour l'utilisateur connecté dans le store
              this.store.dispatch(
                updateCurrentUserSuccess({
                  user: {
                    ...updatedUser.user,
                    sex: formData.sex as 'male' | 'female',
                  },
                })
              );
              this.initialFormData = { ...this.profileForm.value };
              this.isLoading = false;
            },
            error: (error) => {
              this.toastService.error(
                'Erreur lors de la mise à jour du profil. Veuillez réessayer.',
                'Erreur'
              );
              this.isLoading = false;
            },
          });
        } else {
          this.toastService.error('Erreur : Utilisateur non trouvé', 'Erreur');
          this.isLoading = false;
        }
      });
    } else {
      this.toastService.warning(
        'Veuillez corriger les erreurs dans le formulaire',
        'Formulaire invalide'
      );
    }
  }
}
